'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { LogoMark } from './LogoMark';

const NAV_LINKS: { href: string; label: string }[] = [
  { href: '/hire', label: 'For Hiring Teams' },
  { href: '/projects', label: 'Projects' },
  { href: '/about', label: 'About' },
  { href: '/blog', label: 'Blog' },
  { href: '/services', label: 'Services' },
  { href: '/contact', label: 'Contact' },
];

export function Navbar() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  // React 18 doesn't serialize a boolean `inert` prop (it warns and drops it),
  // and @types/react types the prop as boolean so a string won't typecheck.
  // Set the DOM property imperatively so the CLOSED menu is genuinely inert
  // (its links leave the tab order); removed when the menu opens.
  useEffect(() => {
    if (overlayRef.current) overlayRef.current.inert = !menuOpen;
  }, [menuOpen]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
    return () => document.body.classList.remove('menu-open');
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const isActive = (href: string) => pathname === href || pathname.startsWith(`${href}/`);

  if (pathname?.startsWith('/admin')) return null;

  return (
    <header
      role="banner"
      aria-label="Main navigation"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-[400ms] ease-out"
      style={{
        height: 'var(--nav-height)',
        background: scrolled ? 'rgba(11, 10, 8, 0.92)' : 'transparent',
        backdropFilter: scrolled ? 'blur(16px) saturate(140%)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(16px) saturate(140%)' : 'none',
        borderBottom: scrolled ? '1px solid var(--border)' : '1px solid transparent',
      }}
    >
      <div className="mx-auto h-full max-w-content px-6 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2.5 group" aria-label="alejandro.software home">
          <LogoMark size={36} />
          <span className="font-mono text-sm font-semibold tracking-tight hidden sm:inline">
            <span className="text-text">alejandro</span>
            <span className="text-accent">.software</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {NAV_LINKS.map((link) => {
            const active = isActive(link.href);
            return (
              <Link
                key={link.href}
                href={link.href}
                aria-current={active ? 'page' : undefined}
                data-active={active ? 'true' : undefined}
                className="nav-link relative inline-flex items-center font-mono text-xs font-semibold transition-colors duration-200"
                style={{
                  letterSpacing: '0.06em',
                  color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
                  opacity: active ? 1 : 0.8,
                  minHeight: 44,
                  padding: '0 4px',
                  // Dark glyph halo so links survive bright backdrops — on the
                  // homepage the transparent nav sits over the moon's glow and the
                  // upper-right links ("For Hiring Teams", "Contact") fall in its
                  // brightest part (owner visual audit). Harmless once scrolled
                  // (the nav bg goes dark).
                  textShadow: '0 1px 10px rgba(4, 8, 12, 0.55)',
                }}
              >
                {link.label}
              </Link>
            );
          })}
          <Link
            href="/contact"
            className="inline-flex items-center font-sans text-xs rounded-[10px] px-5 transition-all duration-300 hover:-translate-y-px hover:bg-[var(--accent-dim)]"
            style={{
              background: 'transparent',
              color: 'var(--accent)',
              fontWeight: 700,
              minHeight: 44,
              border: '1px solid var(--accent-mid)',
              textShadow: '0 1px 10px rgba(4, 8, 12, 0.55)',
            }}
          >
            Get in touch
          </Link>
        </nav>

        <button
          type="button"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
          className="md:hidden relative w-11 h-11 flex items-center justify-center text-text"
        >
          <span
            className="absolute block h-[2px] w-6 rounded transition-all duration-300"
            style={{
              background: 'var(--text-primary)',
              transform: menuOpen ? 'rotate(45deg)' : 'translateY(-6px)',
            }}
          />
          <span
            className="absolute block h-[2px] w-6 rounded transition-all duration-300"
            style={{
              background: 'var(--text-primary)',
              opacity: menuOpen ? 0 : 1,
            }}
          />
          <span
            className="absolute block h-[2px] w-6 rounded transition-all duration-300"
            style={{
              background: 'var(--text-primary)',
              transform: menuOpen ? 'rotate(-45deg)' : 'translateY(6px)',
            }}
          />
        </button>
      </div>

      {/* Mobile menu overlay */}
      <div
        ref={overlayRef}
        className="md:hidden fixed inset-0 z-40 transition-opacity duration-300"
        style={{
          background: 'rgba(11, 10, 8, 0.98)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          top: 'var(--nav-height)',
          opacity: menuOpen ? 1 : 0,
          pointerEvents: menuOpen ? 'auto' : 'none',
        }}
      >
        <div className="h-full flex flex-col items-center justify-center gap-8 px-6 pb-24">
          {NAV_LINKS.map((link, i) => (
            <Link
              key={link.href}
              href={link.href}
              aria-current={isActive(link.href) ? 'page' : undefined}
              className="text-2xl font-bold transition-colors"
              style={{
                color: isActive(link.href) ? 'var(--accent)' : 'var(--text-primary)',
                opacity: menuOpen ? 1 : 0,
                transform: menuOpen ? 'translateY(0)' : 'translateY(12px)',
                transition: `opacity 0.4s ease ${0.05 * i + 0.1}s, transform 0.4s ease ${0.05 * i + 0.1}s, color 0.2s ease`,
              }}
            >
              {link.label}
            </Link>
          ))}
          <Link
            href="/contact"
            className="mt-6 rounded-[10px] px-8 py-3.5 font-sans text-sm transition-all"
            style={{
              background: 'linear-gradient(135deg, var(--accent), var(--indigo))',
              color: 'var(--bg-primary)',
              fontWeight: 800,
              opacity: menuOpen ? 1 : 0,
              transform: menuOpen ? 'translateY(0)' : 'translateY(12px)',
              transition: `opacity 0.4s ease ${0.05 * NAV_LINKS.length + 0.1}s, transform 0.4s ease ${0.05 * NAV_LINKS.length + 0.1}s`,
            }}
          >
            Get in touch →
          </Link>
          <a
            href="sms:8455448757"
            style={{
              opacity: menuOpen ? 0.7 : 0,
              transform: menuOpen ? 'translateY(0)' : 'translateY(12px)',
              transition: `opacity 0.4s ease ${0.05 * (NAV_LINKS.length + 1) + 0.1}s, transform 0.4s ease ${0.05 * (NAV_LINKS.length + 1) + 0.1}s`,
              color: 'var(--accent)',
              fontSize: 13,
              fontWeight: 600,
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.49 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.4 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.69a16 16 0 0 0 6 6l.76-.76a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z" />
            </svg>
            Text (845) 544-8757
          </a>
        </div>
      </div>
    </header>
  );
}
